/**
 * flashbookReportCard.ts — the report, as a picture somebody can post.
 *
 * A report is a screen; a *shared* report is a thing in somebody else's timeline,
 * read at a glance and often at thumbnail size. So the card is not the report
 * shrunk: it is the four things worth putting on a poster — how the read went, the
 * score, the numbers behind it, and the handful of questions worth another look.
 * Which of those are printed is decided here rather than in the component, so the
 * choice can be tested without rendering anything and the component keeps to
 * layout and wording.
 *
 * Three decisions shape it.
 *
 *  - **Nothing is invented.** A book with nothing to score has no percentage, so
 *    the card prints what the read *was* — pages, time, readings heard — instead of
 *    a `0%` the learner never earned. This is the same rule the report and the
 *    read history follow, and it is deliberately the same shape: a card is a
 *    claim in public, so it may only claim what the register holds.
 *  - **The picture is measured, not hoped for.** A post's image is uploaded as a
 *    blob and a PDS refuses one that is too large, so the capture is asked for at
 *    a size that fits and *falls back* to smaller ones when it does not. The
 *    ladder is here, as arithmetic, because "the picture we post is under the
 *    limit" is only true if something can say so before the request is made.
 *  - **The post is a picture post.** It carries no `type` and no book back-ref: a
 *    post marked `flashbook` is drawn *as a book* by the feed (see
 *    `#/lib/flashbookPostCard`), which would replace the one thing this feature
 *    exists to show with a card for the book itself.
 */
import {POST_IMG_MAX} from '#/lib/constants'
import {type FlashBookReportModel, type FocusEntry} from '#/lib/flashbookReport'

/**
 * The card's own size, in logical pixels.
 *
 * 4:5 — taller than wide, because a feed gives a portrait post a phone's whole
 * width and a book is a portrait thing. Squarer than the reader's 9:16 frame:
 * this is a poster of a read, not a page of the book, and a 9:16 card spends a
 * third of its height on nothing in order to look like a page it is not.
 */
export const REPORT_CARD_SIZE = {width: 480, height: 600}

/** The same ratio as numbers, for the post's own `aspectRatio`. */
export const REPORT_CARD_ASPECT = {width: 4, height: 5}

/**
 * How large the uploaded picture may be.
 *
 * The image limit the composer already targets (see `POST_IMG_MAX`): the same
 * number the app compresses a picked photo against, so a *generated* picture is
 * held to the same budget as a chosen one rather than to a second, private one.
 */
export const REPORT_IMAGE_BUDGET = POST_IMG_MAX.size

/**
 * The capture sizes to try, largest first.
 *
 * Twice the card's own size is the first ask, because that is the size a
 * two-pixel-per-point screen would draw it at anyway — the picture is meant to be
 * *seen*, and text scales badly down. The smaller steps exist for the one case
 * that matters: a PNG of a busy page over a megabyte, where the choice is a
 * slightly softer poster or no post at all.
 */
export const REPORT_IMAGE_SIZES = [
  {width: 960, height: 1200},
  {width: 720, height: 900},
  {width: 540, height: 675},
] as const

/** Which number a tile on the card prints. */
export type ReportCardStatKind =
  /** The score, as a percentage of the points on offer. */
  | 'points'
  /** How many exercises were answered correctly, out of the book's. */
  | 'correct'
  /** How long the read took. */
  | 'time'
  /** How many pages the book has — what was read, when nothing was scored. */
  | 'pages'
  /** How many narrated blocks the learner heard. */
  | 'readings'

/**
 * Whether this read had anything to score.
 *
 * Asked of the model rather than of the band, because the band says how it went
 * and this says whether "how it went" is a question: a scoreless read is
 * `unscored`, and so is a read that answered nothing — and only the first has no
 * percentage to print.
 */
export function reportCardGraded(model: FlashBookReportModel): boolean {
  return model.counts.exercises > 0
}

/**
 * The three numbers the card prints, in the order it prints them.
 *
 * A scored read leads with the score — that is the number a learner came to
 * share — then what it was out of, then how long it took. A scoreless read leads
 * with how *much* was read instead, since nothing else on the card could say what
 * the learner actually did: a card that printed a percentage there would be
 * inventing one.
 *
 * Never more than three, and `time` is dropped rather than padded when the read
 * was not timed: two honest tiles are better than three with one empty.
 */
export function reportCardStats(
  model: FlashBookReportModel,
): ReportCardStatKind[] {
  const graded = reportCardGraded(model)
  const stats: ReportCardStatKind[] = []
  if (graded) stats.push('points')
  if (model.counts.exercises > 0) stats.push('correct')
  if (model.time.totalMs > 0) stats.push('time')
  if (!graded) {
    // Nothing to score: how much book was read is the headline fact, and pages
    // come before the clock because they are what the read *consisted* of.
    stats.unshift('pages')
  }
  if (stats.length < 3 && model.counts.readings > 0) stats.push('readings')
  return stats.slice(0, 3)
}

/**
 * The questions the card names, and how many it could not.
 *
 * The report's own list, in the report's own order (wrong first, then partial,
 * then never reached — see `#/lib/flashbookReport`), cut to what fits on a
 * poster. `more` is carried rather than dropped: a card that showed three and
 * said nothing would claim the learner had only three questions open.
 */
export function reportCardFocus(
  model: FlashBookReportModel,
  limit = 3,
): {rows: FocusEntry[]; more: number} {
  const rows = model.focus.slice(0, Math.max(0, limit))
  return {rows, more: Math.max(0, model.focus.length - rows.length)}
}

/**
 * How many bytes a `data:` URI's payload really is.
 *
 * The capture comes back as base64 — four characters per three bytes, plus the
 * scheme header — so the raw length would read a third larger than the upload
 * that follows it: a picture reported as 1.2MB would be well inside a 1MB budget.
 * Padding is subtracted rather than guessed at, because the encoder is the only
 * thing that knows how many `=` it wrote.
 */
export function dataUriBytes(uri: string): number {
  const comma = uri.indexOf(',')
  const payload = comma === -1 ? uri : uri.slice(comma + 1)
  if (!payload) return 0
  let padding = 0
  if (payload.endsWith('==')) padding = 2
  else if (payload.endsWith('=')) padding = 1
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding)
}

/** Whether a captured picture is small enough for a post's blob. */
export function imageFitsBudget(
  bytes: number,
  budget: number = REPORT_IMAGE_BUDGET,
): boolean {
  return bytes > 0 && bytes <= budget
}

/**
 * The post a shared report is carried by.
 *
 * A plain image post, and the *plainness* is the design: the feed draws a post
 * whose record says `type: 'flashbook'` as a book card (see
 * `#/lib/flashbookPostCard`), which would stand where the report should be. So no
 * marker and no back-ref — the picture is the post.
 *
 * The alt is required by this function rather than optional, because the picture
 * is only ever a *report* to somebody who can see it: the numbers on the card are
 * the whole point, and a learner using a screen reader is being handed a picture
 * of them.
 */
export function flashbookReportPostRecord({
  text,
  createdAt,
  image,
  alt,
}: {
  text: string
  createdAt: string
  /** The uploaded blob, as the PDS answered with it. */
  image: unknown
  alt: string
}) {
  return {
    $type: 'app.bsky.feed.post',
    text,
    createdAt,
    embed: {
      $type: 'app.bsky.embed.images',
      images: [
        {
          image,
          alt,
          aspectRatio: {
            width: REPORT_CARD_ASPECT.width,
            height: REPORT_CARD_ASPECT.height,
          },
        },
      ],
    },
  }
}
